import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import axios from 'axios';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Truck, Hammer, Car, Wrench, Phone, Mail, MapPin, CheckCircle2 } from 'lucide-react';
import { toast } from '../components/ui/sonner';
import { useVehicles } from '../hooks/useVehicles';
import VehicleCard from '../components/VehicleCard';
import LoadingSkeleton from '../components/LoadingSkeleton';

// Secret Admin Link Component
const SecretAdminLink = () => {
  const navigate = useNavigate();
  const timerRef = useRef(null);
  const [isPressed, setIsPressed] = useState(false);

  const handlePressStart = () => {
    setIsPressed(true);
    timerRef.current = setTimeout(() => {
      // Nach 5 Sekunden zum Login navigieren
      navigate('/login');
    }, 5000);
  };

  const handlePressEnd = () => {
    setIsPressed(false);
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
  };

  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);

  return (
    <span
      onMouseDown={handlePressStart}
      onMouseUp={handlePressEnd}
      onMouseLeave={handlePressEnd}
      onTouchStart={handlePressStart}
      onTouchEnd={handlePressEnd}
      style={{
        cursor: 'default',
        userSelect: 'none',
        transition: 'opacity 0.3s',
        opacity: isPressed ? 0.5 : 1
      }}
    >
      Hogir Acar
    </span>
  );
};

const Home = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const contactSectionRef = useRef(null);
  const hasScrolledRef = useRef(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    vehicle: '',
    startDate: '',
    endDate: '',
    message: ''
  });
  
  // Use cached vehicles hook
  const { vehicles: allVehicles, loading: vehiclesLoading } = useVehicles();
  
  // Memoize featured vehicles calculation
  const featuredVehicles = useMemo(() => {
    return allVehicles.filter(v => v.featured === true);
  }, [allVehicles]);

  const handleInputChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  }, []);

  // Handle pre-filled vehicle from navigation state
  useEffect(() => {
    if (location.state?.vehicle && !hasScrolledRef.current) {
      hasScrolledRef.current = true;
      setFormData(prev => ({ ...prev, vehicle: location.state.vehicle }));
    }
    
    // Handle hash navigation to contact section
    if (location.hash === '#kontakt' && contactSectionRef.current) {
      // Wait for content to be fully rendered
      const scrollTimer = setTimeout(() => {
        contactSectionRef.current.scrollIntoView({ 
          behavior: 'instant', // Use instant instead of smooth to avoid multiple scrolls
          block: 'start'
        });
      }, 100);
      
      return () => clearTimeout(scrollTimer);
    }
  }, [location.state, location.hash]);


  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
      
      // Send to backend
      const response = await axios.post(`${API}/contact/`, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        vehicle: formData.vehicle,
        start_date: formData.startDate,
        end_date: formData.endDate,
        message: formData.message
      });
      
      if (response.status === 201) {
        toast.success("Anfrage erfolgreich gesendet!", {
          description: "Wir melden uns schnellstmöglich bei Ihnen. Sie erhalten auch eine Bestätigungs-E-Mail.",
        });
        
        // Reset form
        setFormData({
          name: '',
          email: '',
          phone: '',
          vehicle: '',
          startDate: '',
          endDate: '',
          message: ''
        });
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error("Fehler beim Senden", {
        description: "Bitte versuchen Sie es später erneut oder kontaktieren Sie uns telefonisch.",
      });
    }
  };

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-[#1a1a1a]/95 backdrop-blur-sm border-b border-[#2C3E50]">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <Truck className="h-8 w-8 text-[#FDB913]" />
            <div>
              <h1 className="text-xl font-bold text-white">Hogir Acar</h1>
              <p className="text-xs text-gray-300">Maschinen- & Fahrzeugverleih</p>
            </div>
          </div>
          <nav className="hidden md:flex gap-6">
            <button onClick={() => scrollToSection('leistungen')} className="text-gray-300 hover:text-[#FDB913] transition-colors">
              Leistungen
            </button>
            <button onClick={() => scrollToSection('kontakt')} className="text-gray-300 hover:text-[#FDB913] transition-colors">
              Kontakt
            </button>
          </nav>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1580901369227-308f6f40bdeb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzB8MHwxfHNlYXJjaHwxfHxjb25zdHJ1Y3Rpb24lMjBtYWNoaW5lcnl8ZW58MHx8fHwxNzYwNDgxMTA2fDA&ixlib=rb-4.1.0&q=85)'
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-[#1a1a1a]/95 via-[#2C3E50]/85 to-[#1a1a1a]/70"></div>
        </div>
        
        <div className="relative z-10 container mx-auto px-4 text-center">
          <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 leading-tight">
            Ihr Partner für<br />
            <span className="text-[#FDB913]">Maschinen- & Fahrzeugverleih</span>
          </h1>
          <p className="text-xl md:text-2xl text-gray-200 mb-8 max-w-3xl mx-auto">
            Zuverlässig, flexibel, fair – in Wittlich, Trier, der Region Eifel-Mosel und im Rhein-Main-Gebiet
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg" 
              className="bg-[#FDB913] hover:bg-[#F59E0B] text-[#1a1a1a] font-bold text-lg px-8 py-6 transition-all hover:scale-105"
              onClick={() => scrollToSection('kontakt')}
            >
              Jetzt anfragen
            </Button>
            <Button 
              size="lg" 
              variant="outline"
              className="border-2 border-[#FDB913] text-[#FDB913] hover:bg-[#FDB913] hover:text-[#1a1a1a] font-bold text-lg px-8 py-6 transition-all"
              onClick={() => scrollToSection('leistungen')}
            >
              Unsere Fahrzeuge
            </Button>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="leistungen" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#2C3E50] mb-4">Unsere Leistungen</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Egal ob PKW, Baumaschinen oder LKW – wir haben oder besorgen das Passende für jede Mietdauer
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <Card className="border-2 hover:border-[#FDB913] transition-all hover:shadow-xl group">
              <CardHeader>
                <div className="w-16 h-16 bg-[#FDB913]/10 rounded-xl flex items-center justify-center mb-4 group-hover:bg-[#FDB913]/20 transition-colors">
                  <Car className="h-8 w-8 text-[#FDB913]" />
                </div>
                <CardTitle className="text-2xl text-[#2C3E50]">PKW-Verleih</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-base text-gray-600">
                  Kompaktwagen, Transporter und Limousinen für Privat- und Geschäftskunden.
                </CardDescription>
              </CardContent>
            </Card>

            <Card className="border-2 hover:border-[#FDB913] transition-all hover:shadow-xl group">
              <CardHeader>
                <div className="w-16 h-16 bg-[#FDB913]/10 rounded-xl flex items-center justify-center mb-4 group-hover:bg-[#FDB913]/20 transition-colors">
                  <Truck className="h-8 w-8 text-[#FDB913]" />
                </div>
                <CardTitle className="text-2xl text-[#2C3E50]">LKW-Verleih</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-base text-gray-600">
                  Kipper, Transport- und Lieferfahrzeuge für Baustellen & Logistik.
                </CardDescription>
              </CardContent>
            </Card>

            <Card className="border-2 hover:border-[#FDB913] transition-all hover:shadow-xl group">
              <CardHeader>
                <div className="w-16 h-16 bg-[#FDB913]/10 rounded-xl flex items-center justify-center mb-4 group-hover:bg-[#FDB913]/20 transition-colors">
                  <Hammer className="h-8 w-8 text-[#FDB913]" />
                </div>
                <CardTitle className="text-2xl text-[#2C3E50]">Baumaschinen</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-base text-gray-600">
                  Bagger, Radlader, Rüttelplatten, Anhänger, Stromaggregate uvm.
                </CardDescription>
              </CardContent>
            </Card>

            <Card className="border-2 hover:border-[#FDB913] transition-all hover:shadow-xl group">
              <CardHeader>
                <div className="w-16 h-16 bg-[#FDB913]/10 rounded-xl flex items-center justify-center mb-4 group-hover:bg-[#FDB913]/20 transition-colors">
                  <Wrench className="h-8 w-8 text-[#FDB913]" />
                </div>
                <CardTitle className="text-2xl text-[#2C3E50]">Sonderbeschaffung</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-base text-gray-600">
                  Auf Wunsch organisieren wir Maschinen oder Fahrzeuge, die nicht im Bestand sind.
                </CardDescription>
              </CardContent>
            </Card>
          </div>

          <div className="text-center mt-12">
            <Button 
              size="lg"
              className="bg-[#2C3E50] hover:bg-[#1a1a1a] text-white font-bold px-8 py-6 transition-all hover:scale-105"
              onClick={() => scrollToSection('kontakt')}
            >
              Jetzt unverbindlich anfragen
            </Button>
          </div>
        </div>
      </section>

      {/* Featured Vehicles Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-[#2C3E50] mb-4 text-center">
            Einige unserer Fahrzeuge
          </h2>
          <p className="text-gray-600 mb-12 text-center max-w-2xl mx-auto">
            Entdecken Sie eine Auswahl aus unserem vielfältigen Fuhrpark
          </p>
          
          {vehiclesLoading ? (
            <LoadingSkeleton count={3} />
          ) : featuredVehicles.length > 0 ? (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {featuredVehicles.map((vehicle) => (
                  <VehicleCard key={vehicle.id} vehicle={vehicle} />
                ))}
              </div>

              <div className="text-center mt-12">
                <Button 
                  size="lg" 
                  className="bg-[#FDB913] hover:bg-[#F59E0B] text-[#1a1a1a] font-bold"
                  onClick={() => scrollToSection('kontakt')}
                >
                  Jetzt anfragen
                </Button>
              </div>
            </>
          ) : (
            <div className="text-center py-12">
              <p className="text-gray-500">Keine Fahrzeuge verfügbar</p>
            </div>
          )}
        </div>
      </section>

      {/* Contact Form Section */}
      <section id="kontakt" ref={contactSectionRef} className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl md:text-5xl font-bold text-[#2C3E50] mb-4">Kontakt & Anfrage</h2>
              <p className="text-xl text-gray-600">
                Füllen Sie das Formular aus und wir melden uns schnellstmöglich bei Ihnen
              </p>
            </div>

            <Card className="border-2 shadow-xl">
              <CardContent className="p-8">
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="name">Name *</Label>
                      <Input
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                        className="border-2 focus:border-[#FDB913]"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email">E-Mail *</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        className="border-2 focus:border-[#FDB913]"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="phone">Telefonnummer *</Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleInputChange}
                        required
                        className="border-2 focus:border-[#FDB913]"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="vehicle">Gewünschte Maschine/Fahrzeug *</Label>
                      <Input
                        id="vehicle"
                        name="vehicle"
                        value={formData.vehicle}
                        onChange={handleInputChange}
                        required
                        className="border-2 focus:border-[#FDB913]"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="startDate">Mietbeginn *</Label>
                      <Input
                        id="startDate"
                        name="startDate"
                        type="date"
                        value={formData.startDate}
                        onChange={handleInputChange}
                        required
                        className="border-2 focus:border-[#FDB913]"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="endDate">Mietende *</Label>
                      <Input
                        id="endDate"
                        name="endDate"
                        type="date"
                        value={formData.endDate}
                        onChange={handleInputChange}
                        required
                        className="border-2 focus:border-[#FDB913]"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="message">Nachricht</Label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      rows={5}
                      className="border-2 focus:border-[#FDB913]"
                    />
                  </div>

                  <Button 
                    type="submit" 
                    size="lg"
                    className="w-full bg-[#FDB913] hover:bg-[#F59E0B] text-[#1a1a1a] font-bold text-lg py-6 transition-all hover:scale-105"
                  >
                    Anfrage senden
                  </Button>
                </form>
              </CardContent>
            </Card>

            <div className="mt-12 grid md:grid-cols-3 gap-8 text-center">
              <div className="flex flex-col items-center">
                <div className="w-16 h-16 bg-[#FDB913] rounded-full flex items-center justify-center mb-4">
                  <MapPin className="h-8 w-8 text-[#1a1a1a]" />
                </div>
                <h3 className="font-bold text-[#2C3E50] mb-2">Adresse</h3>
                <p className="text-gray-600">Breslauer Str. 21<br />54516 Wittlich</p>
              </div>
              <div className="flex flex-col items-center">
                <div className="w-16 h-16 bg-[#FDB913] rounded-full flex items-center justify-center mb-4">
                  <Phone className="h-8 w-8 text-[#1a1a1a]" />
                </div>
                <h3 className="font-bold text-[#2C3E50] mb-2">Telefon</h3>
                <p className="text-gray-600">+49 173 4611527</p>
              </div>
              <div className="flex flex-col items-center">
                <div className="w-16 h-16 bg-[#FDB913] rounded-full flex items-center justify-center mb-4">
                  <Mail className="h-8 w-8 text-[#1a1a1a]" />
                </div>
                <h3 className="font-bold text-[#2C3E50] mb-2">E-Mail</h3>
                <p className="text-gray-600">info@hogiracar.com</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl md:text-5xl font-bold text-[#2C3E50] mb-4">Häufige Fragen</h2>
              <p className="text-xl text-gray-600">
                Hier finden Sie Antworten auf die wichtigsten Fragen
              </p>
            </div>

            <Accordion type="single" collapsible className="space-y-4">
              <AccordionItem value="item-1" className="border-2 rounded-lg px-6 hover:border-[#FDB913] transition-colors">
                <AccordionTrigger className="text-lg font-semibold text-[#2C3E50] hover:text-[#FDB913]">
                  Welche Mietdauer ist möglich?
                </AccordionTrigger>
                <AccordionContent className="text-gray-600 text-base">
                  Sowohl Kurzzeit- als auch Langzeitmieten sind flexibel möglich – je nach Bedarf.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-2" className="border-2 rounded-lg px-6 hover:border-[#FDB913] transition-colors">
                <AccordionTrigger className="text-lg font-semibold text-[#2C3E50] hover:text-[#FDB913]">
                  Wird eine Kaution fällig?
                </AccordionTrigger>
                <AccordionContent className="text-gray-600 text-base">
                  Ja, eine angemessene Kaution wird je nach Fahrzeug oder Maschine berechnet und nach Rückgabe vollständig erstattet.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-3" className="border-2 rounded-lg px-6 hover:border-[#FDB913] transition-colors">
                <AccordionTrigger className="text-lg font-semibold text-[#2C3E50] hover:text-[#FDB913]">
                  Können Maschinen geliefert werden?
                </AccordionTrigger>
                <AccordionContent className="text-gray-600 text-base">
                  Auf Wunsch liefern wir Maschinen und Fahrzeuge direkt zum Einsatzort.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-4" className="border-2 rounded-lg px-6 hover:border-[#FDB913] transition-colors">
                <AccordionTrigger className="text-lg font-semibold text-[#2C3E50] hover:text-[#FDB913]">
                  Welche Dokumente werden benötigt?
                </AccordionTrigger>
                <AccordionContent className="text-gray-600 text-base">
                  Gültiger Personalausweis und Führerschein, bei Gewerbekunden auch Gewerbenachweis.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="item-5" className="border-2 rounded-lg px-6 hover:border-[#FDB913] transition-colors">
                <AccordionTrigger className="text-lg font-semibold text-[#2C3E50] hover:text-[#FDB913]">
                  Gibt es eine Versicherung?
                </AccordionTrigger>
                <AccordionContent className="text-gray-600 text-base">
                  Alle Fahrzeuge sind vollkaskoversichert. Zusatzversicherungen sind optional verfügbar.
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#1a1a1a] text-white py-12">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <Truck className="h-8 w-8 text-[#FDB913]" />
                <div>
                  <h3 className="text-xl font-bold">Hogir Acar</h3>
                  <p className="text-sm text-gray-400">Maschinen- & Fahrzeugverleih</p>
                </div>
              </div>
              <p className="text-gray-400 text-sm">
                Ihr zuverlässiger Partner für Maschinen- und Fahrzeugverleih in Wittlich und Umgebung.
              </p>
            </div>
            
            <div>
              <h4 className="font-bold text-lg mb-4 text-[#FDB913]">Leistungen</h4>
              <ul className="space-y-2 text-gray-400">
                <li>PKW-Verleih</li>
                <li>LKW-Verleih</li>
                <li>Baumaschinen</li>
                <li>Sonderbeschaffung</li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-bold text-lg mb-4 text-[#FDB913]">Rechtliches</h4>
              <ul className="space-y-2">
                <li><Link to="/impressum" className="text-gray-400 hover:text-[#FDB913] transition-colors">Impressum</Link></li>
                <li><Link to="/datenschutz" className="text-gray-400 hover:text-[#FDB913] transition-colors">Datenschutz</Link></li>
                <li><Link to="/agb" className="text-gray-400 hover:text-[#FDB913] transition-colors">AGB</Link></li>
                <li><button onClick={() => scrollToSection('faq')} className="text-gray-400 hover:text-[#FDB913] transition-colors">FAQ</button></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-bold text-lg mb-4 text-[#FDB913]">Kontakt</h4>
              <ul className="space-y-2 text-gray-400 text-sm">
                <li>Breslauer Str. 21</li>
                <li>54516 Wittlich</li>
                <li>+49 173 4611527</li>
                <li>info@hogiracar.com</li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-gray-700 pt-8 text-center text-gray-400 text-sm">
            <p className="select-none">© 2025 <SecretAdminLink /> – Maschinen- & Fahrzeugverleih</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;